import { FC, useEffect } from 'react';
import axios from 'axios';
import type { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

export interface AxiosSetupProps { }

let axiosSetupInstalled = false;

const AxiosSetup: FC<AxiosSetupProps> = () => {
  /* eslint-disable @typescript-eslint/no-unsafe-member-access, camelcase */
  useEffect(() => {
    axios.defaults.headers.common['x-ia-user-context'] = 'client';
    axios.defaults.headers.common['x-ia-channel'] = 'web';

    if (axiosSetupInstalled) {
      return;
    }

    // Regression note (branch vs master baseline): previous startup flows could fan out identical
    // GETs (notably version/bootstrap resources) and some consumers were mutating shared response
    // objects. This setup keeps dedupe while returning an isolated payload per consumer.
    axiosSetupInstalled = true;

    type AxiosAdapterResult = AxiosResponse;

    const inFlightGetRequests = new Map<string, Promise<AxiosAdapterResult>>();

    // Fallback deep clone used only when structuredClone is unavailable or cannot clone a payload.
    const fallbackDeepClone = (value: unknown): unknown => {
      if (value === null || value === undefined || typeof value !== 'object') {
        return value;
      }

      if (value instanceof Date) {
        return new Date(value);
      }

      if (Array.isArray(value)) {
        return value.map((item) => fallbackDeepClone(item));
      }

      const source = value as Record<string, unknown>;
      return Object.keys(source).reduce((clone, key) => {
        clone[key] = fallbackDeepClone(source[key]);
        return clone;
      }, {} as Record<string, unknown>);
    };

    const clonePayload = (payload: unknown): unknown => {
      if (payload === undefined || payload === null) {
        return payload;
      }

      if (typeof structuredClone === 'function') {
        try {
          return structuredClone(payload);
        } catch {
          // Fall through to recursive clone for non-structured-cloneable values.
        }
      }

      return fallbackDeepClone(payload);
    };

    const cloneResponse = (response: AxiosAdapterResult): AxiosAdapterResult => ({
      ...response,
      data: clonePayload(response.data),
    });

    const withClonedResponse = (promise: Promise<AxiosAdapterResult>): Promise<AxiosAdapterResult> => promise.then((response) => cloneResponse(response));

    const isAxiosAdapter = (adapter: unknown): adapter is AxiosAdapter => typeof adapter === 'function';

    const resolveAdapter = (config: { adapter?: unknown }): AxiosAdapter | undefined => {
      const explicitAdapter = config.adapter;
      if (isAxiosAdapter(explicitAdapter)) {
        return explicitAdapter;
      }

      const defaultAdapter = axios.defaults.adapter;
      if (isAxiosAdapter(defaultAdapter)) {
        return defaultAdapter;
      }

      const axiosWithGetAdapter = axios as typeof axios & {
        getAdapter?: (adapter: unknown) => AxiosAdapter;
      };

      if (typeof axiosWithGetAdapter.getAdapter === 'function') {
        try {
          return axiosWithGetAdapter.getAdapter(explicitAdapter || defaultAdapter);
        } catch {
          return undefined;
        }
      }

      return undefined;
    };

    const getRequestKey = (config: {
      method?: string;
      baseURL?: string;
      url?: string;
      params?: unknown;
      data?: unknown;
    }): string | undefined => {
      const method = (config.method || 'get').toUpperCase();
      if (method !== 'GET') {
        return undefined;
      }

      const baseURL = config.baseURL || '';
      const url = config.url || '';
      const params = config.params ? JSON.stringify(config.params) : '';
      const data = config.data ? JSON.stringify(config.data) : '';

      return `${method}|${baseURL}|${url}|${params}|${data}`;
    };

    axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const requestKey = getRequestKey(config);

      // Share a single in-flight GET promise per request key to avoid duplicate network calls.
      if (requestKey) {
        const existingPromise = inFlightGetRequests.get(requestKey);
        if (existingPromise !== undefined) {
          // Each subscriber receives a cloned response to avoid cross-consumer mutations.
          config.adapter = (() => withClonedResponse(existingPromise)) as AxiosAdapter;
        } else {
          const adapterFn = resolveAdapter(config);
          if (typeof adapterFn === 'function') {
            // Strip this caller's cancelToken/signal: the underlying call is shared, so cancelling
            // one logical (e.g. superseded) request must not abort it for the other subscribers.
            const { cancelToken, signal, ...sharedRequestConfig } = config;
            const inFlightPromise = Promise
              .resolve(adapterFn(sharedRequestConfig))
              .then((result) => result as AxiosAdapterResult)
              .finally(() => {
                inFlightGetRequests.delete(requestKey);
              });
            inFlightGetRequests.set(requestKey, inFlightPromise);
            config.adapter = (() => withClonedResponse(inFlightPromise)) as AxiosAdapter;
          }
        }
      }

      (config as typeof config & { inFlightRequestKey?: string }).inFlightRequestKey = requestKey;

      return config;
    });

    axios.interceptors.response.use(
      (response) => {
        const requestKey = (response.config as typeof response.config & { inFlightRequestKey?: string }).inFlightRequestKey;
        if (requestKey) {
          inFlightGetRequests.delete(requestKey);
        }

        return response;
      },
      (error: unknown) => {
        const axiosError = error as {
          config?: {
            inFlightRequestKey?: string;
          };
        };
        if (axiosError.config?.inFlightRequestKey) {
          inFlightGetRequests.delete(axiosError.config.inFlightRequestKey);
        }

        return Promise.reject(error);
      },
    );
  }, []);
  /* eslint-enable @typescript-eslint/no-unsafe-member-access, camelcase */
  return null;
};

export default AxiosSetup;
